import React, {
  useRef,
  useEffect,
  useState,
  useMemo,
  forwardRef,
  useContext,
} from 'react'
import { Root, createRoot } from 'react-dom/client'
import { Link } from 'gatsby' // createRootによるReactのRoot内ではintlが使えないためGatsbyのLinkを使う
import { useInView } from 'react-intersection-observer'

import ArtisanTopHeadline from '../ArtisanTopHeadline'
import ArtisanTopDescription from '../ArtisanTopDescription'
import BlurContainer from '../BlurContainer'
import MicrocmsImage from '@atoms/MicrocmsImage'
import LongArrowRitght from '@icons/LongArrowRight'
import ShevronDown from '@icons/ShevronDown'
import MaskedIcon from '@icons/Masked'
import { ArtisanArticle } from '@src/pages/artisan'
import { Coordinates, mapboxglClient, MapboxMap } from '@models/Mapbox'
import { Artisan } from '@models/Artisan'
import {
  buildTargetLocalePath,
  Locale,
  useTranslation,
} from '@models/Translation'
import { Article } from '@models/Article'
import { StoreContext } from '@models/Store'

import * as style from './style.module.sass'

type MapData = Record<
  string,
  {
    workshopObjects: { workshop: string; article: Article }[]
    coordinates: Coordinates
  }
>

type MapSectionProps = {
  artisans: Artisan[]
  artisanArticles: ArtisanArticle[]
}

const MapSection = forwardRef<HTMLElement, MapSectionProps>(
  ({ artisans, artisanArticles }, sectionRef) => {
    const INITIAL_CENTER: [number, number] = useMemo(
      () => [124.825, 35.6288],
      [],
    )
    const INITIAL_ZOOM = 4
    const pcNewPx = 1280

    const { t, locale } = useTranslation()
    const mapData: MapData = useMapData(locale, artisans, artisanArticles)
    // 3つ目の要素はマーカーから選択されたらtrue、リストから選択されたらfalse
    const [selectedItem, setSelectedItem] = useState<[string, number, boolean]>(
      ['', 0, true],
    )
    const [isPcNew, setIsPcNew] = useState<boolean>(true)
    const mapInstance = useRef<MapboxMap>()
    const mapContainer = useRef(null)
    const markerRootRecord = useRef<Record<string, Root>>({})
    const selectedListItem = useRef<HTMLLIElement | null>(null)
    const { ref, inView } = useInView({
      threshold: 0.5,
      triggerOnce: true,
    })

    // マウント時に地図の初期化
    useEffect(() => {
      // マウント時にはisPcNewが適切に設定されていないため使えない
      const isPcNewOnMount = window.innerWidth >= pcNewPx

      mapInstance.current = new mapboxglClient.Map({
        container: mapContainer.current ?? '',
        style: 'mapbox://styles/zozo-next/cluwkr949002q01pp5k5egg95',
        center: INITIAL_CENTER,
        zoom: INITIAL_ZOOM,
        maxBounds: [
          [80, 15],
          [180, 50],
        ],
        dragPan: isPcNewOnMount ? true : false,
        boxZoom: false,
        dragRotate: false,
        keyboard: false,
        doubleClickZoom: false,
        touchZoomRotate: false,
        scrollZoom: false,
      })

      Object.entries(mapData)
        .reverse() // 左から描画することで、ポップアップが上に来るようにする
        .forEach(([location, value]) => {
          if (!mapInstance.current) return
          const element = document.createElement('div')
          new mapboxglClient.Marker(element, { offset: [0, -8] })
            .setLngLat(value.coordinates)
            .addTo(mapInstance.current)
          markerRootRecord.current[location] = createRoot(element)
        })

      document
        .querySelector('a.mapboxgl-ctrl-logo')
        ?.setAttribute('style', 'width: 44px; margin-bottom: -14px;')

      const handleResize = () => {
        setIsPcNew(window.innerWidth >= pcNewPx)
      }
      window.addEventListener('resize', handleResize)

      handleResize()

      return () => {
        mapInstance.current?.remove()
        window.removeEventListener('resize', handleResize)
      }
      // 現状ではINITIAL_CENTERとmapDataの変更はなくマウント時のみに実行したいため
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    // selectedItemに応じてマーカーやポップアップを更新、マップを移動
    useEffect(() => {
      Object.entries(markerRootRecord.current).forEach(([location, root]) => {
        root.render(
          <Marker
            locale={locale}
            selectItem={() => {
              setSelectedItem([
                location,
                selectedItem[0] !== location ? 0 : selectedItem[1],
                true,
              ])
            }}
            selelctedItem={selectedItem}
            location={location}
            workshopObjects={mapData[location].workshopObjects}
          />,
        )
      })

      selectedItem[2] &&
        selectedListItem.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
        })

      const targetData = mapData[selectedItem[0]]
      if (!targetData) return
      const coordinates = targetData.coordinates
      const targetLongtitude = coordinates[0] + (isPcNew ? -4.4 : -1.1)
      const targetLatitude = coordinates[1] + (isPcNew ? 0 : -2.6)
      mapInstance.current?.easeTo({
        center: [targetLongtitude, targetLatitude],
        zoom: isPcNew ? 6.2 : 6,
        duration: 1000,
      })
    }, [isPcNew, locale, mapData, selectedItem])

    // inViewがtrueになったら最初の要素を選択
    useEffect(() => {
      if (!inView) return
      setSelectedItem([Object.entries(mapData)[0][0], 0, false])
      // 基本的にmapDataは変更しないが、一部ブラウザで変化してしまうため含めない
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [inView])

    const handleUnfocus = () => {
      if (!isPcNew) return

      setSelectedItem(['', 0, false])
      mapInstance.current?.easeTo({
        center: INITIAL_CENTER,
        zoom: INITIAL_ZOOM,
        duration: 1000,
      })
    }

    return (
      <section
        className={style.section}
        onClick={handleUnfocus}
        ref={sectionRef}
      >
        <BlurContainer>
          <div className={style.mapArea} ref={ref}>
            <div ref={mapContainer} className={style.mapContainer} />
          </div>
          <div className={style.headline}>
            <ArtisanTopHeadline heading="Location" isRight />
            <div className={style.description}>
              {isPcNew && (
                <ArtisanTopDescription
                  descriptionLines={[
                    t('artisan.location1'),
                    t('artisan.location2'),
                  ]}
                  isRight
                />
              )}
            </div>
            <div className={style.listContainer}>
              <ul className={style.list}>
                {Object.entries(mapData).map(([location, value]) => (
                  <li
                    key={location}
                    className={style.listItem}
                    data-open={selectedItem[0] === location}
                    ref={selectedItem[0] === location ? selectedListItem : null}
                  >
                    <div
                      onMouseEnter={() => setSelectedItem([location, 0, false])}
                    >
                      <LocationItem
                        location={location}
                        workshopObject={value.workshopObjects[0]}
                        locale={locale}
                        selected={
                          selectedItem[0] === location && selectedItem[1] === 0
                        }
                      />
                    </div>
                    {selectedItem[0] === location &&
                      value.workshopObjects
                        .slice(1)
                        .map((workshopObject, index) => {
                          const convertedIndex = index + 1

                          return (
                            <div
                              onMouseEnter={() =>
                                setSelectedItem([
                                  location,
                                  convertedIndex,
                                  false,
                                ])
                              }
                            >
                              <LocationItem
                                location={location}
                                workshopObject={workshopObject}
                                locale={locale}
                                selected={
                                  selectedItem[0] === location &&
                                  selectedItem[1] === convertedIndex
                                }
                                isNested
                              />
                            </div>
                          )
                        })}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </BlurContainer>
      </section>
    )
  },
)

export default MapSection

const useMapData = (
  locale: Locale,
  artisans: Artisan[],
  artisanArticles: ArtisanArticle[],
) =>
  useMemo(() => {
    return artisans.reduce((acc: MapData, artisan) => {
      const artisanArticle = artisanArticles.find(artisanArticle =>
        artisan.blogIds.includes(artisanArticle.article.blogId),
      )
      if (!artisanArticle) return acc

      const location = artisanArticle.location
      if (!acc[location]) {
        acc[location] = {
          coordinates: artisan.coordinates,
          workshopObjects: [],
        }
      }

      const workshop = locale === 'ja' ? artisan.workshop : artisan.workshopEn
      acc[location].workshopObjects.push({
        workshop,
        article: artisanArticle.article,
      })
      return acc
    }, {})
  }, [artisanArticles, artisans, locale])

const Marker: React.FC<{
  locale: Locale
  selectItem: () => void
  selelctedItem: [string, number, boolean]
  location: string
  workshopObjects: { workshop: string; article: Article }[]
}> = ({ locale, selectItem, selelctedItem, location, workshopObjects }) => {
  return (
    <div className={style.markerContainer}>
      <button
        onClick={e => {
          e.stopPropagation()
          selectItem()
        }}
        className={style.marker}
        data-selected={selelctedItem[0] === location}
      >
        <svg
          width="32"
          height="32"
          viewBox="0 0 68 68"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M17.3229 13.8298C26.4514 4.70128 41.2516 4.70128 50.3801 13.8298C59.5086 22.9583 59.5086 37.7585 50.3801 46.8869L33.8515 63.4155L17.3229 46.8869C8.19444 37.7585 8.19444 22.9583 17.3229 13.8298ZM33.8515 37.0369C37.54 37.0369 40.5301 34.0468 40.5301 30.3584C40.5301 26.6699 37.54 23.6798 33.8515 23.6798C30.1631 23.6798 27.173 26.6699 27.173 30.3584C27.173 34.0468 30.1631 37.0369 33.8515 37.0369Z"
            fill="#4F00B5"
          />
        </svg>
      </button>
      {workshopObjects.map((workshopObject, index) => (
        <div
          className={style.popupContainer}
          key={workshopObject.workshop}
          data-show={
            selelctedItem[0] === location && selelctedItem[1] === index
          }
        >
          <Popup
            locale={locale}
            location={location}
            workshop={workshopObject.workshop}
            article={workshopObject.article}
          />
        </div>
      ))}
    </div>
  )
}

const Popup: React.FC<{
  locale: Locale
  location: string
  workshop: string
  article: Article
}> = ({ locale, location, workshop, article }) => {
  const { entity } = useContext(StoreContext)
  const isMasked = entity?.user?.isSignIn !== true && article.isMembersOnly

  return (
    <div className={style.popup}>
      <div className={style.workshop}>{workshop}</div>
      <div className={style.lineContainer}>
        <div className={style.location}>{location}</div>
        <div className={style.title} data-en={locale === 'en'}>
          {article.title}
          {isMasked && <MaskedIcon />}
        </div>
        <Link to={buildTargetLocalePath(locale, article.path)}>
          <div className={style.image}>
            <MicrocmsImage
              image={article.thumbnail}
              alt={article.title}
              type="thumbnail"
              disableAspectRatio
            />
            <div className={style.read}>
              <LongArrowRitght />
              Read
            </div>
          </div>
        </Link>
      </div>
    </div>
  )
}

const LocationItem: React.FC<{
  location: string
  workshopObject: { workshop: string; article: Article }
  locale: Locale
  selected: boolean
  isNested?: boolean
}> = ({ location, workshopObject, locale, selected, isNested = false }) => {
  const locationItemContents = (
    <>
      <span className={style.location} data-visible={!isNested}>
        {location}
      </span>
      <span className={style.line} />
      <span className={style.workshop}>{workshopObject.workshop}</span>
      {!isNested && (
        <div className={style.shevron}>
          <ShevronDown />
        </div>
      )}
    </>
  )

  return (
    <div className={style.locationItem} data-selected={selected}>
      {selected && (
        <Link
          to={buildTargetLocalePath(locale, workshopObject.article.path)}
          className={style.locationItemLink}
          onClick={e => {
            e.stopPropagation()
          }}
        />
      )}
      {locationItemContents}
    </div>
  )
}
